import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { domainClasses, type Domain } from "./domains";

export interface NumberedStepProps {
  n: number;
  title: ReactNode;
  /** One or two everyday sentences. */
  children?: ReactNode;
  /** What kind of step it is; the marker takes the domain tint and ink. */
  domain?: Domain;
  /** `li` inside an `<ol>`; `div` otherwise. */
  as?: "li" | "div";
  titleAs?: "h3" | "h4" | "p";
  /** Layout only. */
  className?: string;
}

/**
 * One numbered step in a list of steps: a round number marker, a bold title
 * and its text. For a 3–4 step explanation with an icon and an action, use
 * `StepCard`; this is the plain list form.
 */
export function NumberedStep({
  n,
  title,
  children,
  domain = "planning",
  as: Tag = "li",
  titleAs: Title = "h3",
  className,
}: NumberedStepProps) {
  const d = domainClasses[domain];
  return (
    <Tag className={cn("flex items-start gap-4", className)}>
      <span
        aria-hidden="true"
        className={cn(
          "flex h-8 w-8 flex-none items-center justify-center rounded-full font-mono text-sm font-bold",
          d.tint,
          d.ink,
        )}
      >
        {n}
      </span>
      <div className="min-w-0">
        <Title className="font-semibold leading-snug text-caire-ink">
          {title}
        </Title>
        {children ? (
          <div className="mt-1 leading-relaxed text-slate">{children}</div>
        ) : null}
      </div>
    </Tag>
  );
}
